import React, { useEffect, useRef, useState } from 'react';
import '../theme/tokens.css';
import './ThemeShowcase.css';
import { Badge, Button } from '../components/ui';

const evidenceRecords = {
  apacHiring: {
    eyebrow: 'Signal GPTN-26-081',
    title: 'APAC hiring momentum is diverging across the peer set',
    status: 'Validated prototype record',
    confidence: 'High confidence',
    summary:
      'The prototype combines public careers pages and company announcements. It does not infer filled positions from open-role counts.',
    observed:
      '499 APAC roles were present across the illustrative collection snapshot; technology and trading roles formed the largest visible clusters.',
    scope: 'APAC public job listings · ten tracked firms',
    period: 'Snapshot dated 02 Aug 2026',
    verified: '02 Aug 2026 · 08:14 AEST',
    sources: [
      ['Public careers pages', '10 raw documents · retrieved 02 Aug 2026'],
      ['Company announcements', '4 typed candidates · retrieved 01–02 Aug 2026'],
      ['GPTN validation run', 'Contract v0.3 · passed deterministic checks'],
    ],
  },
  optiverExpansion: {
    eyebrow: 'Signal GPTN-26-079',
    title: 'Optiver shows sustained APAC technology hiring intent',
    status: 'Under review',
    confidence: 'Medium confidence',
    summary:
      'Open roles show current hiring intent, not net headcount growth. The conclusion remains deliberately narrower than the source signal.',
    observed:
      'The illustrative snapshot contains 13 newly observed APAC technology and trading roles associated with Optiver.',
    scope: 'Display brand: Optiver · APAC roles only',
    period: '30-day change to 02 Aug 2026',
    verified: '02 Aug 2026 · pending second-origin review',
    sources: [
      ['Optiver careers archive', 'Raw document version 6 · public listing metadata'],
      ['GPTN role taxonomy', 'Taxonomy v0.4 · technology/trading classification'],
      ['Review note', 'Entity scope checked; group roll-up not permitted'],
    ],
  },
  imcLeadership: {
    eyebrow: 'Signal GPTN-26-076',
    title: 'IMC adds a senior operational leadership signal in APAC',
    status: 'Validated prototype record',
    confidence: 'High confidence',
    summary:
      'A people movement is treated as an observed event. Strategic meaning is separated as an implication for management review.',
    observed:
      'One senior APAC operational appointment was identified in the illustrative weekly source set.',
    scope: 'IMC display brand · APAC operating scope',
    period: 'Published 29 Jul 2026',
    verified: '01 Aug 2026 · two independent origins',
    sources: [
      ['Company announcement', 'Primary origin · publication time retained'],
      ['Professional profile update', 'Independent corroborating origin'],
      ['GPTN entity resolution', 'Legal entity unresolved; display brand retained'],
    ],
  },
  janeStreet: {
    eyebrow: 'Signal GPTN-26-074',
    title: 'Jane Street role mix shifts toward infrastructure',
    status: 'Methodology check',
    confidence: 'Medium confidence',
    summary:
      'The interface distinguishes a change in advertised role mix from a change in actual workforce allocation.',
    observed:
      'Infrastructure-related roles increased as a share of the illustrative APAC listing snapshot.',
    scope: 'Jane Street public listings · APAC locations',
    period: 'Four-week comparison to 02 Aug 2026',
    verified: '02 Aug 2026 · comparability gate B',
    sources: [
      ['Careers page snapshots', 'Two period-specific raw documents'],
      ['Role classifier output', 'Model output independently sampled'],
      ['Comparability note', 'Location taxonomy changed in one source period'],
    ],
  },
  citadel: {
    eyebrow: 'Signal GPTN-26-069',
    title: 'Citadel Securities signal remains below publication threshold',
    status: 'Insufficient evidence',
    confidence: 'Insufficient evidence',
    summary:
      'No management conclusion is published when the evidence threshold is not met. This is a successful, explicit result.',
    observed:
      'A single source suggested a role-cluster change, but no independent origin was available in the collection window.',
    scope: 'Citadel Securities · APAC public sources',
    period: 'Week ending 02 Aug 2026',
    verified: '02 Aug 2026 · withheld by policy',
    sources: [
      ['Single public source', 'One origin only · corroboration required'],
      ['Validation policy', 'Publication threshold not met'],
      ['Publisher outcome', 'Canonical write correctly withheld'],
    ],
  },
  sig: {
    eyebrow: 'Signal GPTN-26-066',
    title: 'SIG trading-role signal remains exploratory',
    status: 'Early signal',
    confidence: 'Low confidence',
    summary:
      'A small change in visible listings is retained for monitoring, without implying a strategic or workforce shift.',
    observed:
      'Three additional APAC trading roles appear in the illustrative 30-day snapshot.',
    scope: 'SIG display brand · APAC public listings',
    period: '30-day change to 02 Aug 2026',
    verified: '02 Aug 2026 · one source family',
    sources: [
      ['Careers page snapshots', 'Public listing metadata · two collection periods'],
      ['GPTN role taxonomy', 'Trading classification · taxonomy v0.4'],
      ['Validation outcome', 'Monitoring allowed; management escalation withheld'],
    ],
  },
  methodology: {
    eyebrow: 'Methodology note M-014',
    title: 'How GPTN turns a signal into a management claim',
    status: 'Current methodology',
    confidence: 'Version 0.3',
    summary:
      'Source facts remain immutable. Candidates must pass typed validation, evidence and policy gates before a deterministic publisher can create a canonical record.',
    observed:
      'This review UI displays illustrative data only. It is a visual prototype and does not represent approved canonical intelligence.',
    scope: 'GPTN product methodology',
    period: 'Design review · 02 Aug 2026',
    verified: 'Aligned to current repository mandate',
    sources: [
      ['Raw source', 'Original content and retrieval metadata'],
      ['Typed candidate + evidence', 'Structured claim linked to source spans'],
      ['Validation + review', 'Independent checks and policy decision'],
      ['Canonical record', 'Deterministic publication after approval'],
    ],
  },
};

const signals = [
  {
    id: 'optiverExpansion',
    company: 'Optiver',
    category: 'Talent signal',
    age: '6h ago',
    level: 'Material',
    confidence: 'medium',
    headline: 'APAC technology hiring intent remains elevated',
    observed: '13 newly observed technology and trading roles in the 30-day prototype snapshot.',
    implication: 'Capacity may be building around exchange connectivity and production engineering.',
    question: 'Which role clusters persist after the next collection cycle?',
  },
  {
    id: 'imcLeadership',
    company: 'IMC',
    category: 'People move',
    age: '1d ago',
    level: 'Material',
    confidence: 'high',
    headline: 'Senior operational leadership signal in APAC',
    observed: 'One senior appointment verified across two independent public origins.',
    implication: 'Operating-model changes may follow, but no structural change is yet observed.',
    question: 'Does the remit change appear in subsequent hiring or entity disclosures?',
  },
  {
    id: 'janeStreet',
    company: 'Jane Street',
    category: 'Role mix',
    age: '2d ago',
    level: 'Watch',
    confidence: 'medium',
    headline: 'Visible role mix shifts toward infrastructure',
    observed: 'Infrastructure roles form a larger share of current illustrative APAC listings.',
    implication: 'The signal may reflect platform investment; source-period comparability is partial.',
    question: 'Can the location-taxonomy break be normalized before escalation?',
  },
  {
    id: 'citadel',
    company: 'Citadel Securities',
    category: 'Weak signal',
    age: '3d ago',
    level: 'Watch',
    confidence: 'insufficient_evidence',
    headline: 'Role-cluster change held below publication threshold',
    observed: 'One origin was collected; independent corroboration is not yet available.',
    implication: 'No implication published.',
    question: 'Can a genuinely independent source confirm or reject the candidate?',
  },
];

const competitors = [
  { id: 'optiverExpansion', name: 'Optiver', change: '+13', signal: 'Technology hiring', confidence: 'Medium', coverage: 92, period: '30d' },
  { id: 'imcLeadership', name: 'IMC', change: '+8', signal: 'Leadership + talent', confidence: 'High', coverage: 96, period: '30d' },
  { id: 'janeStreet', name: 'Jane Street', change: '+5', signal: 'Infrastructure mix', confidence: 'Medium', coverage: 83, period: '30d' },
  { id: 'citadel', name: 'Citadel Securities', change: '—', signal: 'Below threshold', confidence: 'Insufficient', coverage: 61, period: '30d' },
  { id: 'sig', name: 'SIG', change: '+3', signal: 'Trading roles', confidence: 'Low', coverage: 74, period: '30d' },
];

const roleMix = [
  { label: 'Technology & data', value: 42, count: 208 },
  { label: 'Trading & research', value: 31, count: 154 },
  { label: 'Operations & risk', value: 18, count: 89 },
  { label: 'Corporate functions', value: 9, count: 48 },
];

function Mark() {
  return (
    <span className="rv-mark" aria-hidden="true">
      <span />
      <span />
      <span />
    </span>
  );
}

function Icon({ children }) {
  return <span className="rv-icon" aria-hidden="true">{children}</span>;
}

function EvidenceButton({ record, onOpen, children = 'View evidence' }) {
  return (
    <button
      type="button"
      className="rv-evidence-button"
      onClick={() => onOpen(record)}
      aria-label={`View evidence for ${evidenceRecords[record].title}`}
    >
      <span>{children}</span>
      <span aria-hidden="true">↗</span>
    </button>
  );
}

function AppHeader({ onNavigate }) {
  return (
    <header className="rv-header">
      <a className="rv-brand" href="#top" aria-label="GPTN intelligence home">
        <Mark />
        <span>GPTN</span>
      </a>
      <nav className="rv-nav" aria-label="Review navigation">
        <button type="button" className="is-active" onClick={() => onNavigate('overview')}>Overview</button>
        <button type="button" onClick={() => onNavigate('signals')}>Signals</button>
        <button type="button" onClick={() => onNavigate('competitors')}>Competitors</button>
      </nav>
      <div className="rv-header-actions">
        <span className="rv-freshness"><span aria-hidden="true" /> Live · 8m</span>
        <button type="button" className="rv-search" aria-label="Search the prototype">
          <span aria-hidden="true">⌕</span>
          <span>Search</span>
          <kbd>⌘K</kbd>
        </button>
        <button type="button" className="rv-profile" aria-label="Open reviewer profile">WK</button>
      </div>
    </header>
  );
}

function Hero({ period, onPeriod, onNavigate, onEvidence }) {
  return (
    <section className="rv-hero opt-dark" id="overview" aria-labelledby="review-title">
      <div className="rv-hero-grid">
        <div className="rv-hero-copy">
          <p className="rv-eyebrow"><span>Intelligence brief 32</span><span>02 Aug 2026</span></p>
          <h1 id="review-title">Signal first.<br /><em>Evidence always.</em></h1>
          <p className="rv-hero-lede">
            What changed across the global proprietary trading landscape, why it may matter, and the proof behind every claim.
          </p>
          <div className="rv-hero-actions">
            <Button variant="accent" size="lg" onClick={() => onNavigate('signals')}>Review material moves <span aria-hidden="true">↓</span></Button>
            <Button variant="ghost" size="lg" onClick={() => onEvidence('methodology')}>Read the method</Button>
          </div>
          <p className="rv-prototype-note">Illustrative interface data · not approved canonical intelligence</p>
        </div>

        <aside className="rv-brief-card" aria-label="This week's management brief">
          <div className="rv-brief-topline">
            <span>This week</span>
            <span className="rv-brief-status"><span aria-hidden="true" /> 4 material moves</span>
          </div>
          <p className="rv-brief-number">01</p>
          <h2>APAC hiring momentum is diverging across the peer set.</h2>
          <p>Technology and trading roles form the largest visible clusters, but open roles remain an intent signal—not headcount.</p>
          <div className="rv-brief-meta">
            <Badge confidence="high">High confidence</Badge>
            <span>APAC · 30-day snapshot</span>
          </div>
          <EvidenceButton record="apacHiring" onOpen={onEvidence} />
        </aside>
      </div>

      <div className="rv-hero-footer">
        <dl className="rv-kpis">
          <div><dt>Tracked firms</dt><dd>10</dd><span>target peer set</span></div>
          <div><dt>Fresh signals</dt><dd>30</dd><span>in this review cycle</span></div>
          <div><dt>APAC role signals</dt><dd>499</dd><span>public listings</span></div>
          <div><dt>Evidence coverage</dt><dd>97.4%</dd><span>prototype records</span></div>
        </dl>
        <div className="rv-period" aria-label="Review period">
          {['7d', '30d', '90d'].map((value) => (
            <button
              type="button"
              key={value}
              className={period === value ? 'is-active' : ''}
              aria-pressed={period === value}
              onClick={() => onPeriod(value)}
            >{value}</button>
          ))}
        </div>
      </div>
    </section>
  );
}

function SignalCard({ signal, onEvidence }) {
  return (
    <article className="rv-signal-card">
      <div className="rv-signal-rail">
        <span className={`rv-level rv-level--${signal.level.toLowerCase()}`}>{signal.level}</span>
        <span>{signal.age}</span>
      </div>
      <div className="rv-signal-body">
        <div className="rv-signal-meta">
          <span>{signal.company}</span>
          <span aria-hidden="true">·</span>
          <span>{signal.category}</span>
          <Badge confidence={signal.confidence} />
        </div>
        <h3>{signal.headline}</h3>
        <dl className="rv-claim-stack">
          <div><dt>Observed</dt><dd>{signal.observed}</dd></div>
          <div className="rv-inference"><dt>Implication</dt><dd>{signal.implication}</dd></div>
          <div><dt>Next question</dt><dd>{signal.question}</dd></div>
        </dl>
        <EvidenceButton record={signal.id} onOpen={onEvidence} />
      </div>
    </article>
  );
}

function SignalsSection({ onEvidence }) {
  const [filter, setFilter] = useState('All');
  const visibleSignals = filter === 'All' ? signals : signals.filter((signal) => signal.level === filter);

  return (
    <section className="rv-section" id="signals" aria-labelledby="signals-title">
      <div className="rv-section-header">
        <div>
          <p className="rv-section-kicker">01 · Decision feed</p>
          <h2 id="signals-title">Changes that deserve attention</h2>
          <p>Observed fact, inferred implication, and the next management question stay visibly separate.</p>
        </div>
        <div className="rv-filter" aria-label="Filter signal importance">
          {['All', 'Material', 'Watch'].map((value) => (
            <button
              type="button"
              key={value}
              className={filter === value ? 'is-active' : ''}
              aria-pressed={filter === value}
              onClick={() => setFilter(value)}
            >{value}</button>
          ))}
        </div>
      </div>

      <div className="rv-signal-layout">
        <div className="rv-signal-list" aria-live="polite">
          {visibleSignals.map((signal) => <SignalCard key={signal.id} signal={signal} onEvidence={onEvidence} />)}
        </div>

        <aside className="rv-action-panel">
          <p className="rv-section-kicker">Management lens</p>
          <h3>Three questions for Monday</h3>
          <ol>
            <li><span>01</span><p>Is APAC infrastructure hiring broad-based or concentrated in latency-sensitive teams?</p></li>
            <li><span>02</span><p>Which leadership signals precede measurable operating-model changes?</p></li>
            <li><span>03</span><p>Where does source comparability prevent a peer ranking?</p></li>
          </ol>
          <div className="rv-action-rule" />
          <p className="rv-action-note"><Icon>i</Icon> Recommendations remain reviewer-owned. GPTN does not grant itself publication authority.</p>
        </aside>
      </div>
    </section>
  );
}

function CompetitorTable({ onEvidence }) {
  return (
    <div className="rv-table-wrap">
      <table className="rv-table">
        <caption>Illustrative competitor signal comparison for the selected period</caption>
        <thead>
          <tr>
            <th scope="col">Firm</th>
            <th scope="col">Role change</th>
            <th scope="col">Leading signal</th>
            <th scope="col">Confidence</th>
            <th scope="col">Coverage</th>
            <th scope="col"><span className="rv-sr-only">Evidence</span></th>
          </tr>
        </thead>
        <tbody>
          {competitors.map((row, index) => (
            <tr key={row.name}>
              <th scope="row"><span className="rv-firm-index">{String(index + 1).padStart(2, '0')}</span>{row.name}</th>
              <td className="rv-mono">{row.change === '—' ? <><span>—</span><small> not comparable</small></> : <><strong>{row.change}</strong><small> open roles</small></>}</td>
              <td>{row.signal}</td>
              <td><span className={`rv-confidence-text rv-confidence-text--${row.confidence.toLowerCase()}`}>{row.confidence}</span></td>
              <td>
                <span className="rv-coverage-label">{row.coverage}%</span>
                <span className="rv-coverage" aria-hidden="true"><span style={{ width: `${row.coverage}%` }} /></span>
              </td>
              <td><EvidenceButton record={row.id} onOpen={onEvidence}>Open</EvidenceButton></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CompetitorsSection({ onEvidence }) {
  return (
    <section className="rv-section rv-section--last" id="competitors" aria-labelledby="competitors-title">
      <div className="rv-section-header">
        <div>
          <p className="rv-section-kicker">02 · Competitor pulse</p>
          <h2 id="competitors-title">Comparable where the evidence allows</h2>
          <p>Coverage and comparability travel with the number—never in a hidden methodology footnote.</p>
        </div>
        <span className="rv-asof"><span aria-hidden="true">◷</span> As at 02 Aug · 08:14 AEST</span>
      </div>

      <div className="rv-analysis-grid">
        <div className="rv-analysis-main">
          <CompetitorTable onEvidence={onEvidence} />
        </div>
        <aside className="rv-role-mix">
          <div className="rv-panel-topline">
            <div><p className="rv-section-kicker">APAC role mix</p><h3>499 visible signals</h3></div>
            <span>30d</span>
          </div>
          <div className="rv-role-bars">
            {roleMix.map((role) => (
              <div key={role.label} className="rv-role-row">
                <div><span>{role.label}</span><strong>{role.count}</strong></div>
                <span className="rv-role-track" aria-hidden="true"><span style={{ width: `${role.value}%` }} /></span>
                <small>{role.value}% of visible roles</small>
              </div>
            ))}
          </div>
          <p className="rv-panel-note">Counts describe public listings in the illustrative snapshot. They do not equal hires or net headcount change.</p>
        </aside>
      </div>

      <div className="rv-integrity-bar">
        <div><Icon>✓</Icon><span><strong>Evidence gate passed</strong> 97.4% of prototype claims have a source edge</span></div>
        <div><Icon>≠</Icon><span><strong>Comparability protected</strong> missing periods render as “—”, never zero</span></div>
        <div><Icon>↗</Icon><span><strong>One-click provenance</strong> every decision fact opens its source chain</span></div>
      </div>
    </section>
  );
}

function EvidenceDrawer({ recordKey, onClose }) {
  const closeRef = useRef(null);
  const previousFocus = useRef(null);
  const record = recordKey ? evidenceRecords[recordKey] : null;

  useEffect(() => {
    if (!record) return undefined;
    previousFocus.current = document.activeElement;
    closeRef.current?.focus();
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      previousFocus.current?.focus?.();
    };
  }, [record, onClose]);

  if (!record) return null;

  return (
    <div className="rv-drawer-layer">
      <button type="button" className="rv-drawer-scrim" aria-label="Dismiss evidence overlay" onClick={onClose} />
      <aside className="rv-drawer" role="dialog" aria-modal="true" aria-labelledby="drawer-title">
        <div className="rv-drawer-header">
          <div><p>{record.eyebrow}</p><span>{record.status}</span></div>
          <button ref={closeRef} type="button" className="rv-drawer-close" onClick={onClose} aria-label="Close evidence panel">×</button>
        </div>
        <div className="rv-drawer-content">
          <h2 id="drawer-title">{record.title}</h2>
          <p className="rv-drawer-summary">{record.summary}</p>
          <div className="rv-drawer-observed">
            <span>Observed statement</span>
            <p>{record.observed}</p>
          </div>
          <dl className="rv-drawer-meta">
            <div><dt>Confidence</dt><dd>{record.confidence}</dd></div>
            <div><dt>Scope</dt><dd>{record.scope}</dd></div>
            <div><dt>Period</dt><dd>{record.period}</dd></div>
            <div><dt>Verified</dt><dd>{record.verified}</dd></div>
          </dl>
          <section className="rv-source-chain" aria-labelledby="source-chain-title">
            <div className="rv-source-title"><span>Source chain</span><strong>{record.sources.length} layers</strong></div>
            <h3 id="source-chain-title" className="rv-sr-only">Source chain</h3>
            <ol>
              {record.sources.map(([title, detail], index) => (
                <li key={title}>
                  <span>{String(index + 1).padStart(2, '0')}</span>
                  <div><strong>{title}</strong><p>{detail}</p></div>
                  <span aria-hidden="true">✓</span>
                </li>
              ))}
            </ol>
          </section>
          <div className="rv-drawer-policy">
            <Icon>i</Icon>
            <p><strong>Prototype policy</strong>This visual review uses illustrative records only. No source, candidate, or canonical data is mutated.</p>
          </div>
        </div>
        <div className="rv-drawer-footer">
          <span>Audit trail available</span>
          <Button variant="primary" size="md" onClick={onClose}>Return to brief</Button>
        </div>
      </aside>
    </div>
  );
}

export default function ThemeShowcase() {
  const [period, setPeriod] = useState('30d');
  const [evidence, setEvidence] = useState(null);

  const navigate = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  useEffect(() => {
    document.documentElement.classList.add('rv-review-active');
    return () => document.documentElement.classList.remove('rv-review-active');
  }, []);

  return (
    <div className="rv-page" id="top">
      <AppHeader onNavigate={navigate} />
      <main>
        <Hero period={period} onPeriod={setPeriod} onNavigate={navigate} onEvidence={setEvidence} />
        <div className="rv-workbench">
          <SignalsSection onEvidence={setEvidence} />
          <CompetitorsSection onEvidence={setEvidence} />
        </div>
      </main>
      <footer className="rv-footer">
        <div className="rv-brand"><Mark /><span>GPTN</span></div>
        <p>Evidence-first competitor intelligence · Optiver-inspired review concept</p>
        <span>Internal prototype · 02 Aug 2026</span>
      </footer>
      <EvidenceDrawer recordKey={evidence} onClose={() => setEvidence(null)} />
    </div>
  );
}
